import { useCallback, useEffect } from 'react';
import { usePartnerDataClient } from '../api/use-partner-data-client';
import { DEFAULT_PAGE_SIZE } from '../constants/partner-data-gateway.constant';
import { ConfigurationStatus } from '../domain/enums';
import { Configuration, PaginatedResult } from '../domain/types';
import { useAsyncResource } from './use-async-resource';
import { useCursorPagination } from './use-cursor-pagination';

export interface UseConfigurationsParams {
  status?: ConfigurationStatus;
  dataProvider?: string;
  limit?: number;
}

export interface UseConfigurationsReturn {
  configurations: Configuration[];
  isLoading: boolean;
  hasError: boolean;
  hasNext: boolean;
  hasPrevious: boolean;
  currentPage: number;
  goToNext: () => void;
  goToPrevious: () => void;
  /**
   * Re-runs the current page's fetch. Passed to the mutation hooks in
   * `use-configuration-mutations.ts` as their `onSuccess` so a write shows
   * up in the list immediately.
   */
  refetch: () => void;
}

const EMPTY_CONFIGURATIONS_PAGE: PaginatedResult<Configuration> = {
  items: [],
  pageInfo: { hasNextPage: false, nextCursor: null },
};

/**
 * Fetches a page of configurations through the `PartnerDataClient` port.
 *
 * Read-only by design: the create/update/status mutations live in
 * `use-configuration-mutations.ts` so the pages that write can own their
 * own saving and error state without every list consumer inheriting it.
 */
export const useConfigurations = (
  params: UseConfigurationsParams = {},
): UseConfigurationsReturn => {
  const { status, dataProvider, limit = DEFAULT_PAGE_SIZE } = params;
  const client = usePartnerDataClient();
  const { cursor, hasPrevious, currentPage, goToNext, goToPrevious, reset } =
    useCursorPagination();

  const fetchConfigurations = useCallback(
    () => client.listConfigurations({ cursor, limit, status, dataProvider }),
    [client, cursor, limit, status, dataProvider],
  );

  const { data, isLoading, hasError, refetch } = useAsyncResource(
    fetchConfigurations,
    EMPTY_CONFIGURATIONS_PAGE,
  );

  // Reset to the first page whenever the filters (but not the cursor) change,
  // so a filtered result never starts partway through the previous result's
  // cursor stack.
  useEffect(() => {
    reset();
  }, [status, dataProvider, limit, reset]);

  return {
    configurations: data.items,
    isLoading,
    hasError,
    hasNext: data.pageInfo.hasNextPage,
    hasPrevious,
    currentPage,
    goToNext: (): void => goToNext(data.pageInfo),
    goToPrevious,
    refetch,
  };
};
